import type {
  AggregationColumnOptionsByObjectType,
  AggregationFilterOptionByObjectType,
  AggregationPublicVersionByObjectType,
  ClientQueryDataConfigGetObjectsResponse,
  ClientQueryDataConfigObjectVersion,
} from '../../store/engine/types';
import type { ServiceAttributesById, Services } from '../../store/engine/types/schema';
import {
  AGGREGATION_SERVICE_KEY_IN_SERVICES,
  AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME,
} from 'flow/service/services/constants';

type ConfigObjectDataByObjectType = {
  aggregationFilterOptionsByObjectType: AggregationFilterOptionByObjectType;
  columnOptionsByObjectType: AggregationColumnOptionsByObjectType;
  publicVersionByObjectType: AggregationPublicVersionByObjectType;
};

const helpers = {
  extractConfigObjectDataByObjectType: ({
    clientQueryDataConfigObjects,
  }: {
    clientQueryDataConfigObjects: ClientQueryDataConfigGetObjectsResponse['data_objects'];
  }): ConfigObjectDataByObjectType => clientQueryDataConfigObjects
    .reduce((acc, configObject) => {
      const activeConfigObjectVersion = helpers.getActiveConfigObjectVersion({
        configObjectVersions: configObject.versions,
      });

      if (!activeConfigObjectVersion) {
        return acc;
      }

      const { object_slug: objectSlug } = configObject;

      acc.aggregationFilterOptionsByObjectType[objectSlug] = activeConfigObjectVersion
        .columns
        .filter(column => column.user_filterable)
        .map(column => ({
          label: column.display_name,
          value: column.column_slug,
        }));

      acc.columnOptionsByObjectType[objectSlug] = activeConfigObjectVersion.columns
        .filter(column => column.user_aggregable)
        .map(column => ({
          label: column.display_name,
          value: column.column_slug,
          aggregations: column.aggregations,
        }));

      acc.publicVersionByObjectType[objectSlug] = activeConfigObjectVersion.public_version;

      return acc;
    }, {
      aggregationFilterOptionsByObjectType: {},
      columnOptionsByObjectType: {},
      publicVersionByObjectType: {},
    } as ConfigObjectDataByObjectType),

  formatAggregationServiceAttributesById: ({
    aggregationFilterOptionsByObjectType,
    aggregationServiceAttributesById,
    columnOptionsByObjectType,
    publicVersionByObjectType,
  }: {
    aggregationFilterOptionsByObjectType: AggregationFilterOptionByObjectType;
    aggregationServiceAttributesById: ServiceAttributesById;
    columnOptionsByObjectType: AggregationColumnOptionsByObjectType;
    publicVersionByObjectType: AggregationPublicVersionByObjectType;
  }): ServiceAttributesById => Object.fromEntries(Object
    .entries(
      aggregationServiceAttributesById,
    )
    .map(([attributeId, attribute]) => {
      const objectType = AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME[attribute.attribute_name];

      if (!objectType) {
        return [attributeId, attribute];
      }

      return [
        attributeId,
        {
          ...attribute,
          aggregationColumnOptions: (
            columnOptionsByObjectType[objectType]
          ),
          aggregationFilterOptions: (
            aggregationFilterOptionsByObjectType[objectType]
          ),
          aggregationObjectVersionPublicVersion: (
            publicVersionByObjectType[objectType]
          ),
        },
      ];
    }),
  ),

  getActiveConfigObjectVersion: ({
    configObjectVersions,
  }: {
    configObjectVersions: ClientQueryDataConfigObjectVersion[];
  }): ClientQueryDataConfigObjectVersion | undefined => (
    configObjectVersions.find(version => version.active_selects)
  ),

  getAggregationServiceId: (
    { servicesById }: { servicesById: Services },
  ): number | undefined => Object
    .values(servicesById)
    .find(service => (
      service.internal_name === AGGREGATION_SERVICE_KEY_IN_SERVICES),
    )
    ?.id,
};

export const enrichAggregationServiceAttributes = ({
  clientQueryDataConfigObjects,
  servicesById,
}: {
  clientQueryDataConfigObjects: ClientQueryDataConfigGetObjectsResponse['data_objects'];
  servicesById: Services;
}): Services => {
  const aggregationsServiceId = helpers.getAggregationServiceId({ servicesById });

  if (!aggregationsServiceId) {
    return servicesById;
  }

  const aggregationServiceAttributesById = servicesById[aggregationsServiceId].attributes;

  if (!aggregationServiceAttributesById) {
    return servicesById;
  }

  const {
    aggregationFilterOptionsByObjectType,
    columnOptionsByObjectType,
    publicVersionByObjectType,
  } = helpers.extractConfigObjectDataByObjectType({ clientQueryDataConfigObjects });

  const aggregationServiceAttributesWithConfigObjectsById = helpers
    .formatAggregationServiceAttributesById({
      aggregationFilterOptionsByObjectType,
      aggregationServiceAttributesById,
      columnOptionsByObjectType,
      publicVersionByObjectType,
    });

  return {
    ...servicesById,
    [aggregationsServiceId]: {
      ...servicesById[aggregationsServiceId],
      attributes: aggregationServiceAttributesWithConfigObjectsById,
    },
  };
};
